import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Progress } from '@/components/ui/progress';
import { Loader2, Plus, GripVertical, Mail, MapPin, Sparkles, Settings, X, MessageSquare, Target, Brain, Briefcase, MapPinned, DollarSign } from 'lucide-react';
import { KanbanColumn, KanbanCandidate, JobStage } from './types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CommunicationPanel } from '@/components/communication';
import { cn } from '@/lib/utils';

interface KanbanBoardProps {
  columns: KanbanColumn[];
  unstagedCandidates: KanbanCandidate[];
  loading: boolean;
  onMoveCandidate: (applicationId: string, newStageId: string) => Promise<boolean>;
  onAddCandidate: () => void;
  onUpdateStage: (stageId: string, updates: Partial<JobStage>) => Promise<boolean>;
  onAddStage: (name: string) => Promise<JobStage | null>;
  onDeleteStage: (stageId: string) => Promise<boolean>;
  onCandidateClick?: (candidate: KanbanCandidate) => void;
  selectionMode?: boolean;
  selectedCandidates?: KanbanCandidate[];
  onToggleSelection?: (candidate: KanbanCandidate) => void;
  onSelectAll?: (candidates: KanbanCandidate[]) => void;
  onClearSelection?: () => void;
}

export function KanbanBoard({
  columns,
  unstagedCandidates,
  loading,
  onMoveCandidate,
  onAddCandidate,
  onUpdateStage,
  onAddStage,
  onDeleteStage,
  onCandidateClick,
  selectionMode = false,
  selectedCandidates = [],
  onToggleSelection,
  onSelectAll,
  onClearSelection,
}: KanbanBoardProps) {
  const [draggedCandidate, setDraggedCandidate] = useState<KanbanCandidate | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [newStageName, setNewStageName] = useState('');
  const [addingStage, setAddingStage] = useState(false);
  const [communicationCandidate, setCommunicationCandidate] = useState<KanbanCandidate | null>(null);

  const isSelected = (candidateId: string) => selectedCandidates.some(c => c.id === candidateId);

  const handleDragStart = (e: React.DragEvent, candidate: KanbanCandidate) => {
    setDraggedCandidate(candidate);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStageId(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStageId(null);
  };

  const handleDrop = async (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDragOverStageId(null);

    if (draggedCandidate && draggedCandidate.stage_id !== stageId) {
      await onMoveCandidate(draggedCandidate.application_id, stageId);
    }
    setDraggedCandidate(null);
  };

  const handleStageNameSave = async (stageId: string, name: string) => {
    if (name.trim()) {
      await onUpdateStage(stageId, { name: name.trim() });
    }
    setEditingStageId(null);
  };

  const handleAddNewStage = async () => {
    if (newStageName.trim()) {
      await onAddStage(newStageName.trim());
      setNewStageName('');
      setAddingStage(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const allColumns = [...columns];
  
  // Add unstaged candidates as first column if any
  if (unstagedCandidates.length > 0) {
    allColumns.unshift({
      stage: {
        id: 'unstaged',
        job_id: '',
        name: 'Unassigned',
        stage_type: 'applied',
        position: -1,
        color: '#94a3b8',
      },
      candidates: unstagedCandidates,
    });
  }

  return (
    <>
      <ScrollArea className="w-full">
        <div className="flex gap-4 pb-4 min-w-max">
          {allColumns.map(column => (
            <div
              key={column.stage.id}
              className={cn(
                'w-72 flex-shrink-0 rounded-lg border bg-muted/30 transition-colors',
                dragOverStageId === column.stage.id && 'ring-2 ring-primary bg-primary/5'
              )}
              onDragOver={(e) => column.stage.id !== 'unstaged' && handleDragOver(e, column.stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => column.stage.id !== 'unstaged' && handleDrop(e, column.stage.id)}
            >
              {/* Column Header */}
              <div
                className="flex items-center justify-between p-3 border-b"
                style={{ borderLeftColor: column.stage.color, borderLeftWidth: 4 }}
              >
                {editingStageId === column.stage.id ? (
                  <Input
                    autoFocus
                    defaultValue={column.stage.name}
                    className="h-7 text-sm"
                    onBlur={(e) => handleStageNameSave(column.stage.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleStageNameSave(column.stage.id, e.currentTarget.value);
                      } else if (e.key === 'Escape') {
                        setEditingStageId(null);
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{column.stage.name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {column.candidates.length}
                    </Badge>
                  </div>
                )}

                {column.stage.id !== 'unstaged' && (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => setEditingStageId(column.stage.id)}
                    >
                      <Settings className="h-3 w-3" />
                    </Button>
                    {column.stage.stage_type === 'custom' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => onDeleteStage(column.stage.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Candidates */}
              <ScrollArea className="h-[calc(100vh-320px)]">
                <div className="p-2 space-y-2">
                  {column.candidates.map(candidate => (
                    <Card
                      key={candidate.application_id}
                      className={cn(
                        'p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow',
                        draggedCandidate?.application_id === candidate.application_id && 'opacity-50',
                        selectionMode && isSelected(candidate.id) && 'ring-2 ring-primary bg-primary/5'
                      )}
                      draggable={!selectionMode}
                      onDragStart={(e) => !selectionMode && handleDragStart(e, candidate)}
                      onDragEnd={() => setDraggedCandidate(null)}
                    >
                      <div 
                        className="cursor-pointer"
                        onClick={() => {
                          if (selectionMode) {
                            onToggleSelection?.(candidate);
                          } else {
                            onCandidateClick?.(candidate);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-1">
                            {selectionMode ? (
                              <Checkbox
                                checked={isSelected(candidate.id)}
                                onCheckedChange={() => onToggleSelection?.(candidate)}
                                onClick={(e) => e.stopPropagation()}
                                className="mr-1"
                              />
                            ) : (
                              <GripVertical className="h-3 w-3 text-muted-foreground" />
                            )}
                            <span className="font-medium text-sm">
                              {candidate.first_name} {candidate.last_name}
                            </span>
                          </div>
                          {candidate.match_score !== null && candidate.match_score !== undefined && (
                            <HoverCard openDelay={200}>
                              <HoverCardTrigger asChild>
                                <Badge 
                                  variant={candidate.match_score >= 70 ? "default" : candidate.match_score >= 40 ? "secondary" : "outline"}
                                  className={cn(
                                    "text-xs flex items-center gap-1 cursor-help",
                                    candidate.match_score >= 70 && "bg-green-600 hover:bg-green-700",
                                    candidate.match_score >= 40 && candidate.match_score < 70 && "bg-amber-500 hover:bg-amber-600 text-white"
                                  )}
                                >
                                  <Target className="h-3 w-3" />
                                  {candidate.match_score}%
                                </Badge>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-80 p-4" side="right" align="start">
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between border-b pb-2">
                                    <div className="flex items-center gap-2">
                                      <Brain className="h-4 w-4 text-primary" />
                                      <span className="font-semibold text-sm">Match Score Breakdown</span>
                                    </div>
                                    <span className="text-lg font-bold">{candidate.match_score}%</span>
                                  </div>
                                  
                                  {candidate.score_details ? (
                                    <div className="space-y-3">
                                      <ScoreRow 
                                        icon={Sparkles} 
                                        label="Skills Match" 
                                        score={candidate.score_details.skill_score ?? 0} 
                                      />
                                      <ScoreRow 
                                        icon={Briefcase} 
                                        label="Experience" 
                                        score={candidate.score_details.experience_score ?? 0} 
                                      />
                                      <ScoreRow 
                                        icon={MapPinned} 
                                        label="Location" 
                                        score={candidate.score_details.location_score ?? 0} 
                                      />
                                      <ScoreRow 
                                        icon={DollarSign} 
                                        label="Salary Fit" 
                                        score={candidate.score_details.salary_score ?? 0} 
                                      />
                                      
                                      {candidate.score_details.ai_reasoning && (
                                        <div className="pt-2 border-t">
                                          <p className="text-xs text-muted-foreground leading-relaxed">
                                            {candidate.score_details.ai_reasoning}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-muted-foreground py-2">Score breakdown not available</p>
                                  )}
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          )}
                        </div>

                        {candidate.email_primary && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{candidate.email_primary}</span>
                          </div>
                        )}

                        {candidate.city && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                            <MapPin className="h-3 w-3" />
                            <span>{candidate.city}</span>
                          </div>
                        )}

                        {candidate.ai_skills_summary && (
                          <div className="flex items-start gap-1 text-xs text-muted-foreground bg-muted/50 rounded p-2 mb-2">
                            <Sparkles className="h-3 w-3 mt-0.5 text-primary" />
                            <span className="line-clamp-2">{candidate.ai_skills_summary}</span>
                          </div>
                        )}
                      </div>

                      {/* Communication Button - only show when not in selection mode */}
                      {!selectionMode && (
                        <div className="flex gap-1 pt-2 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 flex-1 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCommunicationCandidate(candidate);
                            }}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Message
                          </Button>
                        </div>
                      )}
                    </Card>
                  ))}

                  {column.candidates.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No candidates
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          ))}

          {/* Add Stage Column */}
          <div className="w-72 flex-shrink-0">
            {addingStage ? (
              <div className="rounded-lg border bg-muted/30 p-3">
                <Input
                  autoFocus
                  placeholder="Stage name..."
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddNewStage();
                    if (e.key === 'Escape') {
                      setAddingStage(false);
                      setNewStageName('');
                    }
                  }}
                  className="mb-2"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddNewStage}>
                    Add
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setAddingStage(false);
                      setNewStageName('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full h-12 border-dashed"
                onClick={() => setAddingStage(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Stage
              </Button>
            )}
          </div>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Communication Dialog */}
      <Dialog
        open={!!communicationCandidate}
        onOpenChange={() => setCommunicationCandidate(null)}
      >
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              Message {communicationCandidate?.first_name} {communicationCandidate?.last_name}
            </DialogTitle>
            <DialogDescription>
              Send a message or log communication
            </DialogDescription>
          </DialogHeader>
          {communicationCandidate && (
            <CommunicationPanel
              candidateId={communicationCandidate.id}
              candidate={{
                id: communicationCandidate.id,
                firstName: communicationCandidate.first_name || undefined,
                lastName: communicationCandidate.last_name || undefined,
                email: communicationCandidate.email_primary || undefined,
                phone: communicationCandidate.phone_primary || undefined,
              }}
              className="border-0"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ScoreRow({ icon: Icon, label, score }: { icon: any; label: string; score: number }) {
  const getBarColor = (s: number) => {
    if (s >= 70) return "bg-green-500";
    if (s >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          <span>{label}</span>
        </div>
        <span className="font-semibold">{Math.round(score)}%</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${getBarColor(score)} rounded-full transition-all`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}
